'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { CrossIcon } from '@curology/ui-components-web/react/Icon/icons/CrossIcon';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useState } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import {
  type ShippingAddressFields,
  type SmartySuggestion,
} from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import { getSuggestions, splitAddressStringIntoParts } from '../js/smarty';

const ShippingAddressAutocompleteTextInput: React.FC<{
  fieldEnteredFieldName: string;
  fieldEnteredOrderNumber: number;
  format?: RegExp;
  id: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  setShippingAddressFields: React.Dispatch<
    React.SetStateAction<ShippingAddressFields>
  >;
  shippingAddressFields: ShippingAddressFields;
}> = ({
  fieldEnteredFieldName,
  fieldEnteredOrderNumber,
  format,
  id,
  label,
  placeholder,
  required,
  setShippingAddressFields,
  shippingAddressFields,
}) => {
  const { brand } = useFlowContext();
  const [hasBeenInteractedWith, setHasBeenInteractedWith] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<SmartySuggestion[]>([]);

  const canShowAnError = hasBeenInteractedWith && !isFocused;

  const showRequiredError =
    canShowAnError && required && shippingAddressFields[id].trim() === '';

  let hasFormatError = false;

  if (format) {
    hasFormatError =
      shippingAddressFields[id].trim() !== '' &&
      !format.test(shippingAddressFields[id]);
  }

  const showFormatError = canShowAnError && hasFormatError;

  let helperText;

  if (showRequiredError) {
    helperText = `The ${label} field is required.`;
  }

  if (showFormatError) {
    helperText = `The ${label} field is invalid.`;
  }

  const showError = showRequiredError || showFormatError;
  const state = showError ? 'critical' : '';

  return (
    <>
      <TextInput
        helperText={helperText}
        id={id}
        label={label}
        onBlur={(event) => {
          setIsFocused(false);

          trackEvent(AnalyticsEvent.FIELD_ENTERED, {
            field: fieldEnteredFieldName,
            orderNumber: fieldEnteredOrderNumber,
            value: event.target.value.length.toString(),
          });
        }}
        onChange={async (e) => {
          setShippingAddressFields({
            ...shippingAddressFields,
            [id]: e.target.value,
          });

          getSuggestions(brand, e.target.value, setSuggestions);
        }}
        onFocus={() => {
          setHasBeenInteractedWith(true);
          setIsFocused(true);

          const domElem = document.querySelector(`#${id}`);

          if (domElem) {
            domElem.scrollIntoView({
              behavior: 'smooth',
            });
          }
        }}
        placeholder={placeholder}
        required={required}
        state={state}
        value={shippingAddressFields[id]}
      />
      {suggestions.length > 0 && (
        <div className="cur-relative data-hj-suppress">
          <div className="cur-absolute cur-bg-surface-neutral cur-border cur-border-disabled cur-p-3 cur-top-[-1px] cur-w-full cur-z-10">
            <div className="cur-flex cur-items-center cur-justify-between">
              <Typography className="cur-text-disabled">Suggestions</Typography>
              <Button
                buttonType="text"
                data-sentry-id="Close address pre-fill suggestions"
                iconStart={CrossIcon}
                onClick={() => setSuggestions([])}
                size="sm"
                state="dark"
              />
            </div>
            <ul className="cur-mb-0 cur-mt-3">
              {suggestions.map((suggestion, index) => {
                const {
                  city,
                  state,
                  street_line: streetAddress,
                  zipcode: zipCode,
                } = suggestion;

                const searchString = shippingAddressFields[id];
                const addressString = `${streetAddress}, ${city}, ${state} ${zipCode}`;

                const addressStringParts = splitAddressStringIntoParts(
                  searchString,
                  addressString,
                );

                return (
                  <li key={index}>
                    <button
                      className="cur-overflow-hidden cur-py-2 cur-text-ellipsis cur-text-left cur-w-full cur-whitespace-nowrap"
                      data-sentry-id="Select address pre-fill suggestion"
                      onClick={() => {
                        setShippingAddressFields({
                          ...shippingAddressFields,
                          city,
                          state,
                          streetAddress,
                          zipCode,
                        });

                        setSuggestions([]);

                        trackEvent(AnalyticsEvent.ADDRESS_SUGGESTION_SELECTED);
                      }}
                      type="button"
                    >
                      {addressStringParts.map((addressStringPart, index) => {
                        return (
                          <Typography
                            className="cur-inline"
                            key={index}
                            weight={
                              addressStringPart.style === 'bold'
                                ? 'bold'
                                : 'normal'
                            }
                          >
                            {addressStringPart.substring}
                          </Typography>
                        );
                      })}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default ShippingAddressAutocompleteTextInput;
